import { supabase } from '../supabaseClient';
import { roleService, type StaffAccess } from './roleService';

export type AppUserKind = 'admin' | 'manager' | 'guard' | 'client' | 'deactivated' | 'unknown';

/**
 * Kinds that get the operations workspace. The kind is the tier the database
 * summarises somebody's roles into: `admin` holds System Administrator,
 * `manager` holds some other management role. What a manager's sidebar shows
 * comes from `access`, not from the kind.
 */
export const WORKSPACE_USER_KINDS: AppUserKind[] = ['admin', 'manager'];

export const isWorkspaceUser = (kind: AppUserKind | undefined): boolean => (
  kind === 'admin' || kind === 'manager'
);

export interface AppUserContext {
  kind: AppUserKind;
  userId: string;
  fullName: string | null;
  companyId?: string | null;
  clientContractId?: string | null;
  /** Workspace users only: their roles and the areas those open. */
  access?: StaffAccess;
}

interface ProfileContextRow {
  id: string;
  company_id: string | null;
  role: string;
  full_name: string | null;
  status: string | null;
}

interface ClientUserContextRow {
  id: string;
  company_id: string;
  client_contract_id: string;
  full_name: string | null;
  status: string;
}

export const appUserService = {
  async getCurrentUserContext(userId?: string): Promise<AppUserContext | null> {
    const resolvedUserId = userId ?? (await supabase.auth.getUser()).data.user?.id;
    if (!resolvedUserId) return null;

    const [{ data: profile, error: profileError }, { data: clientUser, error: clientError }] = await Promise.all([
      supabase
        .from('profiles')
        .select('id, company_id, role, full_name, status')
        .eq('id', resolvedUserId)
        .maybeSingle(),
      supabase
        .from('client_users')
        .select('id, company_id, client_contract_id, full_name, status')
        .eq('id', resolvedUserId)
        .maybeSingle(),
    ]);

    if (profileError) throw profileError;
    if (clientError) throw clientError;

    if (profile) {
      const row = profile as unknown as ProfileContextRow;
      // A deactivated account cannot sign in or refresh, but a session issued
      // before it was switched off lives on until its token expires. This is
      // what turns that session away in the meantime.
      if (row.status === 'disabled') {
        return {
          kind: 'deactivated',
          userId: row.id,
          fullName: row.full_name ?? null,
          companyId: row.company_id ?? null,
        };
      }

      const kind: AppUserKind = row.role === 'admin' || row.role === 'manager' || row.role === 'guard'
        ? row.role
        : 'unknown';

      return {
        kind,
        userId: row.id,
        fullName: row.full_name ?? null,
        companyId: row.company_id ?? null,
        // Somebody who holds a guard role and a management role is a manager
        // here: the workspace is theirs, and the field app still is too.
        ...(kind === 'admin' || kind === 'manager'
          ? { access: await roleService.fetchCurrentAccess(kind) }
          : {}),
      };
    }

    if (clientUser) {
      const row = clientUser as unknown as ClientUserContextRow;
      return {
        kind: row.status === 'active' ? 'client' : 'unknown',
        userId: row.id,
        fullName: row.full_name ?? null,
        companyId: row.company_id,
        clientContractId: row.client_contract_id,
      };
    }

    return {
      kind: 'unknown',
      userId: resolvedUserId,
      fullName: null,
    };
  },
};
